import { FormBlockInstance } from '../api-types';

export const TEXT_AREA_TYPE = 'text_area';
export const TEXT_AREA_TYPE_VERSION = 1;

/** Mirrors pro-core's own default, so the field is never unbounded if config arrives thin. */
export const DEFAULT_MAX_LENGTH = 1000;

const DEFAULT_ROWS = 4;

export interface TextAreaConfig {
  maxLength: number;
  minLength: number | null;
  /** Guidance shown while typing, distinct from the hard maximum. Null when not configured. */
  softBudget: number | null;
  rows: number;
}

/** The answer shape pro-core's `text_area@1` validates. */
export interface TextAreaAnswer {
  text: string;
}

const asRecord = (value: unknown): Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};

const asPositiveInt = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) && value > 0
    ? Math.floor(value)
    : null;

export const readTextAreaConfig = (
  block: FormBlockInstance
): TextAreaConfig => {
  const config = asRecord(block.config);
  const interaction = asRecord(config.interaction);
  const presentation = asRecord(config.presentation);

  return {
    maxLength: asPositiveInt(interaction.maxLength) ?? DEFAULT_MAX_LENGTH,
    minLength: asPositiveInt(interaction.minLength),
    softBudget: asPositiveInt(interaction.softBudget),
    rows: asPositiveInt(presentation.rows) ?? DEFAULT_ROWS
  };
};

export const readTextAreaAnswer = (answer: unknown): TextAreaAnswer | null => {
  const record = asRecord(answer);
  return typeof record.text === 'string' ? { text: record.text } : null;
};

/**
 * Answered means something other than whitespace was typed, and that it clears any configured
 * minimum.
 *
 * Both checks mirror the server's: it treats a blank answer as absent and refuses anything under the
 * minimum, so gating Next on the same rules means Next never enables on an answer submit would then
 * reject. As everywhere in this runner, this is immediate feedback only — the server revalidates and
 * its verdict wins.
 */
export const isTextAreaAnswered = (
  answer: unknown,
  config: TextAreaConfig
): boolean => {
  const parsed = readTextAreaAnswer(answer);
  const text = parsed?.text.trim() ?? '';
  if (!text.length) {
    return false;
  }
  return config.minLength === null || text.length >= config.minLength;
};
